import React, { useMemo, useState } from "react";
import { Col, Divider, Input, InputNumber, Row, Select, Switch, Tooltip, Typography } from "antd";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { PermissionedButton as PermissionedButtonBase } from "@components/PermissionedComponents";
const PermissionedButton = PermissionedButtonBase as React.ComponentType<any>;
import MetricConfigBuilder from "./MetricConfigBuilder";
import { IUDMConfig, IUserDefinedMetric, METRIC_TYPE_OPTIONS } from "./constants";
import { toBareFormula, toQuotedFormula } from "./utils";

const { Text } = Typography;

export interface MetricFormProps {
    initial: IUserDefinedMetric;
    isExpanded?: boolean;
    saving: boolean;
    onSave: (data: IUserDefinedMetric) => void;
    onCancel: () => void;
}

const MetricForm: React.FC<MetricFormProps> = ({
    initial,
    isExpanded: _isExpanded,
    saving,
    onSave,
    onCancel,
}) => {
    // The editor works with bare operand tokens (op1); the backend stores them quoted.
    // Strip on load, re-quote on save (below) so the editor mirrors the flag condition UX.
    const [form, setForm] = useState<IUserDefinedMetric>(() => ({
        ...initial,
        config: {
            ...initial.config,
            formula: toBareFormula(initial.config?.formula ?? ""),
        },
    }));
    const [formulaValid, setFormulaValid] = useState(false);

    const colorStrategies: any[] = useSelector(({ team }: any) => team.color_strategies);
    const strategyVariables: any[] = useSelector(
        ({ team }: any) => team.misc?.color_strategy_variables ?? []
    );

    const variableLabels = useMemo(
        () => Object.fromEntries(strategyVariables.map((v: any) => [v.key, v.label])),
        [strategyVariables]
    );

    const colorStrategyOptions = useMemo(
        () =>
            (colorStrategies ?? []).map((s) => {
                const disallowed: string[] = s.disallowed_udm_variables ?? [];
                return {
                    value: s.id,
                    label: s.name,
                    disabled: disallowed.length > 0,
                    disallowedLabels: disallowed.map((k) => variableLabels[k] ?? k),
                };
            }),
        [colorStrategies, variableLabels]
    );

    const isDirty = useMemo(() => {
        const rawConfig: IUDMConfig = initial.config ?? { formula: "", fetch_params: {} };
        // `form.config.formula` is bare; compare against a bare baseline so an unedited
        // existing metric doesn't read as dirty on open.
        const initialConfig: IUDMConfig = { ...rawConfig, formula: toBareFormula(rawConfig.formula ?? "") };
        return (
            form.name !== initial.name ||
            form.metric_type !== initial.metric_type ||
            form.decimal_places !== initial.decimal_places ||
            form.color_strategy !== initial.color_strategy ||
            form.is_active !== initial.is_active ||
            form.is_visible !== initial.is_visible ||
            JSON.stringify(form.config) !== JSON.stringify(initialConfig)
        );
    }, [form, initial]);

    const isValid = useMemo(() => {
        if (!form.name.trim()) return false;
        if (form.color_strategy == null) return false;
        const formula = form.config?.formula?.trim() ?? "";
        if (!formula) return false;
        const fetchParams = form.config?.fetch_params ?? {};
        const operandKeys = Object.keys(fetchParams);
        if (operandKeys.length === 0) return false;
        const allHaveIdentifier = operandKeys.every(
            (k) => !!fetchParams[k]?.metric_identifier?.trim()
        );
        if (!allHaveIdentifier) return false;
        return formulaValid;
    }, [form.name, form.color_strategy, form.config, formulaValid]);

    return (
        <div className="udm-form">
            <Row gutter={[16, 12]}>
                <Col span={10}>
                    <div className="udm-form-field">
                        <Text strong>Name</Text>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="Metric name"
                            maxLength={150}
                        />
                    </div>
                </Col>
                <Col span={8}>
                    <div className="udm-form-field">
                        <Text strong>Metric Type</Text>
                        <Select
                            value={form.metric_type}
                            options={METRIC_TYPE_OPTIONS}
                            onChange={(value) => setForm((f) => ({ ...f, metric_type: value }))}
                            style={{ width: "100%" }}
                        />
                    </div>
                </Col>
                <Col span={3}>
                    <div className="udm-form-field udm-form-field--label-overflow">
                        <Text strong>Decimal Places</Text>
                        <InputNumber
                            value={form.decimal_places ?? null}
                            min={0}
                            max={7}
                            onChange={(value) =>
                                setForm((f) => ({ ...f, decimal_places: value }))
                            }
                            style={{ width: "100%" }}
                        />
                    </div>
                </Col>
                <Col span={12}>
                    <div className="udm-form-field">
                        <Text strong>Color Strategy <Text type="danger">*</Text></Text>
                        <Select
                            value={form.color_strategy ?? undefined}
                            options={colorStrategyOptions}
                            optionRender={(option: any) =>
                                option.data.disabled ? (
                                    <Tooltip
                                        title={`Uses ${option.data.disallowedLabels.join(", ")} — custom metrics only support strategies without these variables.`}
                                    >
                                        <div>{option.data.label}</div>
                                    </Tooltip>
                                ) : (
                                    option.data.label
                                )
                            }
                            onChange={(value) =>
                                setForm((f) => ({ ...f, color_strategy: value ?? null }))
                            }
                            style={{ width: "100%" }}
                            placeholder="Select color strategy"
                        />
                    </div>
                </Col>
                <Col span={6}>
                    <div className="udm-form-field">
                        <Text strong>Visible</Text>
                        <div style={{ paddingTop: 6 }}>
                            <Switch
                                checked={form.is_visible}
                                onChange={(checked) => setForm((f) => ({ ...f, is_visible: checked }))}
                            />
                        </div>
                    </div>
                </Col>
            </Row>

            <Divider style={{ margin: "12px 0" }} />

            <MetricConfigBuilder
                config={form.config}
                onChange={(config) => setForm((f) => ({ ...f, config }))}
                onFormulaValidation={setFormulaValid}
                currentMetricId={initial.id}
                decimalPlaces={form.decimal_places}
            />

            <div className="udm-form-actions">
                <PermissionedButton
                    permissionCheck="ManageUserDefinedMetrics"
                    permissionMessage="You don't have permission to manage user defined metrics"
                    onClick={onCancel}
                    icon={<CloseOutlined />}
                >
                    Cancel
                </PermissionedButton>
                <PermissionedButton
                    permissionCheck="ManageUserDefinedMetrics"
                    permissionMessage="You don't have permission to manage user defined metrics"
                    type="primary"
                    onClick={() => onSave({ ...form, config: { ...form.config, formula: toQuotedFormula(form.config.formula) } })}
                    icon={<SaveOutlined />}
                    loading={saving}
                    disabled={!isValid}
                    className={isDirty && isValid ? "pulse" : ""}
                >
                    Save
                </PermissionedButton>
            </div>
        </div>
    );
};

export default MetricForm;
